import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ListEndpointsQuery } from '../../../types/case-container'
import {
  type ActiveFilter,
  type FilterDefinition,
  filtersToQuery,
} from './case-infrastructure-tab.helpers'

export interface SearchFilterState {
  searchInput: string
  setSearchInput: React.Dispatch<React.SetStateAction<string>>
  activeSearch: string
  activeFilters: ActiveFilter[]
  showFilterDropdown: boolean
  setShowFilterDropdown: React.Dispatch<React.SetStateAction<boolean>>
  selectedFilterField: FilterDefinition | null
  setSelectedFilterField: React.Dispatch<React.SetStateAction<FilterDefinition | null>>
  filterDropdownRef: React.RefObject<HTMLDivElement>
  queryParams: ListEndpointsQuery
  availableFilters: FilterDefinition[]
  executeSearch: () => void
  handleSearchKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  clearSearch: () => void
  addFilter: (fieldDef: FilterDefinition, value: string) => void
  removeFilter: (fieldId: keyof ListEndpointsQuery) => void
  clearAllFilters: () => void
}

interface FilterCallbacksArgs {
  setActiveFilters: React.Dispatch<React.SetStateAction<ActiveFilter[]>>
  setActiveSearch: React.Dispatch<React.SetStateAction<string>>
  setSearchInput: React.Dispatch<React.SetStateAction<string>>
  setShowFilterDropdown: React.Dispatch<React.SetStateAction<boolean>>
  setSelectedFilterField: React.Dispatch<React.SetStateAction<FilterDefinition | null>>
}

interface FilterCallbacks {
  addFilter: (fieldDef: FilterDefinition, value: string) => void
  removeFilter: (fieldId: keyof ListEndpointsQuery) => void
  clearAllFilters: () => void
}

function useFilterCallbacks(args: FilterCallbacksArgs): FilterCallbacks {
  const {
    setActiveFilters,
    setActiveSearch,
    setSearchInput,
    setShowFilterDropdown,
    setSelectedFilterField,
  } = args

  const addFilter = useCallback(
    (fieldDef: FilterDefinition, value: string) => {
      const option = fieldDef.options.find((opt) => opt.value === value)
      if (!option) return
      setActiveFilters((prev) => {
        const filtered = prev.filter((filt) => filt.fieldId !== fieldDef.id)
        return [
          ...filtered,
          { fieldId: fieldDef.id, fieldLabel: fieldDef.label, value, valueLabel: option.label },
        ]
      })
      setShowFilterDropdown(false)
      setSelectedFilterField(null)
    },
    [setActiveFilters, setShowFilterDropdown, setSelectedFilterField]
  )

  const removeFilter = useCallback(
    (fieldId: keyof ListEndpointsQuery) => {
      setActiveFilters((prev) => prev.filter((filt) => filt.fieldId !== fieldId))
    },
    [setActiveFilters]
  )

  const clearAllFilters = useCallback(() => {
    setActiveFilters([])
    setActiveSearch('')
    setSearchInput('')
  }, [setActiveFilters, setActiveSearch, setSearchInput])

  return { addFilter, removeFilter, clearAllFilters }
}

function useDropdownClickOutside(
  ref: React.RefObject<HTMLDivElement>,
  setShowFilterDropdown: React.Dispatch<React.SetStateAction<boolean>>,
  setSelectedFilterField: React.Dispatch<React.SetStateAction<FilterDefinition | null>>
): void {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setShowFilterDropdown(false)
        setSelectedFilterField(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [ref, setShowFilterDropdown, setSelectedFilterField])
}

export function useCaseInfrastructureSearch(
  filterDefinitions: FilterDefinition[]
): SearchFilterState {
  const [searchInput, setSearchInput] = useState('')
  const [activeSearch, setActiveSearch] = useState('')
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([])
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)
  const [selectedFilterField, setSelectedFilterField] = useState<FilterDefinition | null>(null)
  const filterDropdownRef = useRef<HTMLDivElement>(null)

  const queryParams = useMemo(
    () => filtersToQuery(activeFilters, activeSearch || undefined),
    [activeFilters, activeSearch]
  )

  const availableFilters = useMemo(() => {
    const activeFieldIds = new Set(activeFilters.map((filt) => filt.fieldId))
    return filterDefinitions.filter((def) => !activeFieldIds.has(def.id))
  }, [activeFilters, filterDefinitions])

  const executeSearch = useCallback(() => {
    setActiveSearch(searchInput.trim())
  }, [searchInput])

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        executeSearch()
      }
    },
    [executeSearch]
  )

  const clearSearch = useCallback(() => {
    setSearchInput('')
    setActiveSearch('')
  }, [])

  const filterCallbacks = useFilterCallbacks({
    setActiveFilters,
    setActiveSearch,
    setSearchInput,
    setShowFilterDropdown,
    setSelectedFilterField,
  })

  useDropdownClickOutside(filterDropdownRef, setShowFilterDropdown, setSelectedFilterField)

  return {
    searchInput,
    setSearchInput,
    activeSearch,
    activeFilters,
    showFilterDropdown,
    setShowFilterDropdown,
    selectedFilterField,
    setSelectedFilterField,
    filterDropdownRef,
    queryParams,
    availableFilters,
    executeSearch,
    handleSearchKeyDown,
    clearSearch,
    ...filterCallbacks,
  }
}
